// Matches the `limit: 8` in sections/predictive-search.liquid.
const PREDICTIVE_SEARCH_RESOURCE_LIMIT = 8;

// hrefs carry per-query tracking params, so pathname is the stable identity.
function predictiveSearchItemKey(element) {
  const link = element.querySelector('a[href]');
  return link ? link.pathname : element.textContent.trim();
}

// Keyed across renders, so retyping only fades in what changed.
const PREDICTIVE_SEARCH_ANIMATED_UNITS = [
  ['.predictive-search__query-item', (element) => `query:${element.textContent.trim()}`],
  ['.predictive-search__list-item', (element) => `product:${predictiveSearchItemKey(element)}`],
  ['.predictive-search__products-header', () => 'products-header'],
  ['.predictive-search__no-results-message', (element) => `no-results:${element.textContent.trim()}`],
  ['.predictive-search__fallback', () => 'fallback'],
];

// Matched to .predictive-search's height transition in component-predictive-search.css.
const PREDICTIVE_SEARCH_SHIFT_DURATION = 320;
const PREDICTIVE_SEARCH_SHIFT_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

// Smaller differences are sub-pixel type measurement, not a layout change.
const PREDICTIVE_SEARCH_SHIFT_THRESHOLD = 1;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isInteractingWithResults = false;
    this.resultsHeightCleanup = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
  this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

  // React to an emptied field now rather than after SearchForm's 300ms debounce.
  this.input.addEventListener('input', this.onInputCleared.bind(this));

  this.input.addEventListener('focus', this.onFocus.bind(this));
  this.addEventListener('focusout', this.onFocusOut.bind(this));
  this.addEventListener('keyup', this.onKeyup.bind(this));
  this.addEventListener('keydown', this.onKeydown.bind(this));

  this.predictiveSearchResults.addEventListener('pointerdown', () => {
    this.isInteractingWithResults = true;
  });

  this.predictiveSearchResults.addEventListener('pointerup', () => {
    setTimeout(() => {
      this.isInteractingWithResults = false;
    }, 100);
  });
}

  getQuery() {
    return this.input.value.trim();
  }

  onInputCleared() {
    if (this.getQuery().length) return;

    // Drop any in-flight request, or its results can re-open the panel over an empty field.
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.toggleResetButton();
    this.close(true);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
  if (this.isInteractingWithResults) return;

  // Inside the open modal a blur is no reason to close; the modal's own close resets state.
  if (this.closest('details')?.hasAttribute('open')) return;

  const nextFocusedElement = event.relatedTarget;

  if (nextFocusedElement && this.contains(nextFocusedElement)) {
    return;
  }

  setTimeout(() => {
    if (
      !this.isInteractingWithResults &&
      !this.contains(document.activeElement)
    ) {
      this.close();
    }
  }, 150);
}

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // offsetParent filters out hidden (duplicated) elements.
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&resources[limit]=${PREDICTIVE_SEARCH_RESOURCE_LIMIT}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Measured before the swap: the height the drop animates from (0 when closed).
    const previousHeight = this.predictiveSearchResults.getBoundingClientRect().height;

    const previousKeys = this.collectAnimationKeys();

    // Measured before the swap: the outgoing row is gone once the new markup is in.
    const previousSuggestionHeight = this.suggestionRowHeight();

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.markChangedResults(previousKeys);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.animateResultsHeight(previousHeight);

    // After animateResultsHeight, which must read the final layout, not a transform.
    this.animateSuggestionShift(previousHeight, previousSuggestionHeight);
  }

  // Includes the row's bottom margin, which travels with it.
  suggestionRowHeight() {
    const row = this.predictiveSearchResults.querySelector('.predictive-search__query-list');

    if (!row || !row.getClientRects().length) return 0;

    return row.offsetHeight + (parseFloat(window.getComputedStyle(row).marginBottom) || 0);
  }

  // Direct children, not #predictive-search-results: the Liquid closes that div early when there are products.
  animateSuggestionShift(previousPanelHeight, previousRowHeight) {
    // Nothing was on screen: animateResultsHeight already carries the open.
    if (previousPanelHeight <= 0) return;

    const delta = previousRowHeight - this.suggestionRowHeight();

    if (Math.abs(delta) < PREDICTIVE_SEARCH_SHIFT_THRESHOLD) return;

    // The CSS fade-in has its own reduced-motion query; script must check too.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // animate(), not an inline transform: the fade-in's `both` fill outranks inline styles.
    Array.from(this.predictiveSearchResults.children).forEach((child) => {
      // The section's own <link> tag and the live-region <span> have no box.
      if (!child.getClientRects().length) return;

      child.animate([{ transform: `translateY(${delta}px)` }, { transform: 'none' }], {
        duration: PREDICTIVE_SEARCH_SHIFT_DURATION,
        easing: PREDICTIVE_SEARCH_SHIFT_EASING,
      });
    });
  }

  collectAnimationKeys() {
    const keys = new Set();

    PREDICTIVE_SEARCH_ANIMATED_UNITS.forEach(([selector, keyFor]) => {
      this.predictiveSearchResults.querySelectorAll(selector).forEach((element) => {
        // The no-results blocks are always in the markup and hidden by CSS, so presence is not enough.
        if (!element.getClientRects().length) return;
        keys.add(keyFor(element));
      });
    });

    return keys;
  }

  markChangedResults(previousKeys) {
    PREDICTIVE_SEARCH_ANIMATED_UNITS.forEach(([selector, keyFor]) => {
      this.predictiveSearchResults.querySelectorAll(selector).forEach((element) => {
        if (!previousKeys.has(keyFor(element))) element.classList.add('predictive-search__animate-in');
      });
    });
  }

  // Without letting the change animate: the caller is setting up its own transition.
  measureResultsNaturalHeight() {
    const element = this.predictiveSearchResults;
    const currentHeight = element.style.height;
    element.style.height = 'auto';
    const naturalHeight = element.scrollHeight;
    element.style.height = currentHeight;
    return naturalHeight;
  }

  // .search-modal's height follows this box, so animating it resizes the whole modal.
  animateResultsHeight(fromHeight) {
    this.runResultsHeightTransition(fromHeight, this.measureResultsNaturalHeight());
  }

  // onFinish runs only if the transition reaches its target; a preempting one skips it.
  runResultsHeightTransition(fromHeight, targetHeight, { onFinish } = {}) {
    const element = this.predictiveSearchResults;

    // A rapid retype can land mid-animation; only one may own the cleanup.
    this.resultsHeightCleanup?.();

    element.style.transition = 'none';
    element.style.overflow = 'hidden';
    element.style.height = `${fromHeight}px`;
    // Commit the start height, or the browser coalesces both writes.
    void element.offsetHeight;
    element.style.transition = '';
    element.style.height = `${targetHeight}px`;

    const cleanup = ({ finished = false } = {}) => {
      element.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallbackTimer);
      this.resultsHeightCleanup = null;
      // Back to auto so later reflows are not clipped by a stale pixel height.
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
      if (finished) onFinish?.();
    };

    const onTransitionEnd = (event) => {
      // Ignore transitions bubbling up from the cards inside the panel.
      if (event.target !== element || event.propertyName !== 'height') return;
      cleanup({ finished: true });
    };

    // transitionend never fires under reduced motion or for an unchanged height.
    const fallbackTimer = setTimeout(() => cleanup({ finished: true }), 600);
    this.resultsHeightCleanup = () => cleanup();
    element.addEventListener('transitionend', onTransitionEnd);
  }

  setLiveRegionResults() {
  this.removeAttribute('loading');

  const liveRegionCount = this.querySelector(
    '[data-predictive-search-live-region-count-value]'
  );

  if (liveRegionCount) {
    this.setLiveRegionText(liveRegionCount.textContent);
  }
}

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  // Full reset for when the search modal itself closes.
  resetState() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isInteractingWithResults = false;

    this.close(true);
    this.toggleResetButton();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) this.input.value = '';

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', false);

    // A running collapse owns the close and its finalize re-checks the field; restarting it delays the default links.
    const isCollapsing = this.resultsHeightCleanup !== null && this.predictiveSearchResults.style.height === '0px';
    if (isCollapsing && this.closest('details')?.hasAttribute('open')) return;

    // [open] keeps the panel displayed, so closing visuals wait for the collapse.
    const finalize = () => {
      // Checked at finalize: the fetch-error path closes without clearSearchTerm.
      if (clearSearchTerm || !this.getQuery().length) {
        this.removeAttribute('results');
        // Drop the markup too, or the next loading state flashes the previous results.
        this.predictiveSearchResults.innerHTML = '';
      }
      this.removeAttribute('open');
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
    };

    const currentHeight = this.predictiveSearchResults.getBoundingClientRect().height;

    // Smooth collapse while the modal is open; instant when the modal itself is closing.
    if (currentHeight > 0 && this.closest('details')?.hasAttribute('open')) {
      this.runResultsHeightTransition(currentHeight, 0, { onFinish: finalize });
      return;
    }

    // Let any in-flight animation tear down first; finalize's removeAttribute('style') would wipe its state.
    this.resultsHeightCleanup?.();
    finalize();
  }
}

customElements.define('predictive-search', PredictiveSearch);
